import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Lock, MessageSquare, Send, Trash2, Unlock, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, API, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { reloadCatalog, templateDefaults, useCatalog } from "@/lib/catalog";
import { Logo } from "@/components/Logo";
import { ConsentCheck, LegalLink } from "@/components/Legal";

const BYPASS = "fchem_dev_bypass";

export const MaintenanceGate = ({ children }) => {
  const [st, setSt] = useState(null);
  useEffect(() => { fetch(`${API}/site/status`).then((r) => r.json()).then(setSt).catch(() => setSt({})); }, []);
  const devPath = window.location.pathname.startsWith("/gelistirici/");
  if (!st) return null;
  if (!st.maintenance || devPath || localStorage.getItem(BYPASS)) return children;
  return (
    <div className="min-h-screen grid place-items-center p-6 text-center" data-testid="maintenance-page">
      <div className="max-w-xl space-y-6">
        <div className="flex justify-center"><Logo /></div>
        <Wrench className="h-14 w-14 mx-auto text-[#FACC15]" />
        <h1 className="font-display text-4xl font-bold">Bakımdayız</h1>
        <p className="text-lg text-slate-300 leading-relaxed" data-testid="maintenance-message">{st.message}</p>
      </div>
    </div>
  );
};

export const DevAccessPage = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    api.post("/site/bypass", { key }).then(() => { localStorage.setItem(BYPASS, "1"); navigate("/ogretmen/giris", { replace: true }); })
      .catch(() => navigate("/", { replace: true }));
  }, [key, navigate]);
  return <p className="p-10 eyebrow">Geliştirici erişimi doğrulanıyor…</p>;
};

export const FeedbackDialog = ({ open, onOpenChange }) => {
  const [msg, setMsg] = useState("");
  const [ok, setOk] = useState(false);
  const send = async () => {
    try { await api.post("/feedback", { message: msg }); toast.success("Geri bildiriminiz için teşekkür ederiz!"); setMsg(""); onOpenChange(false); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="feedback-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Geri bildirim gönder</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400">İlk izlenimlerinizi, karşılaştığınız sorunları veya geliştirme önerilerinizi yazın. Yanıtımız bildirim olarak gelir.</p>
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={6} maxLength={3000} placeholder="Görüş ve önerileriniz…" data-testid="feedback-message" />
        <ConsentCheck checked={ok} onChange={setOk} testId="feedback-kvkk">İletişim talebimle ilgili <LegalLink kind="kvkk" />'ni okudum.</ConsentCheck>
        <DialogFooter><Button onClick={send} disabled={msg.trim().length < 3 || !ok} className="font-bold" data-testid="feedback-send"><Send className="h-4 w-4 mr-2" />Gönder</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const openFeedback = () => window.dispatchEvent(new Event("fchem-feedback"));
export const FeedbackHost = () => {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const f = () => setOpen(true);
    window.addEventListener("fchem-feedback", f);
    return () => window.removeEventListener("fchem-feedback", f);
  }, []);
  return <FeedbackDialog open={open} onOpenChange={setOpen} />;
};

const FeedbackItem = ({ f, onChange }) => {
  const [reply, setReply] = useState("");
  const send = async () => {
    try { await api.post(`/feedback/${f.id}/reply`, { message: reply }); setReply(""); toast.success("Yanıt öğretmene bildirim olarak gönderildi"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-4 space-y-2" data-testid={`feedback-${f.id}`}>
      <div className="flex items-start gap-2">
        <p className="text-sm flex-1"><b>{f.name}</b> <span className="text-slate-400">{f.email} · {new Date(f.at).toLocaleString("tr-TR")}</span></p>
        <Button size="sm" variant="ghost" className="text-red-400 h-7" onClick={() => api.delete(`/feedback/${f.id}`).then(onChange)} data-testid={`feedback-delete-${f.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <p className="whitespace-pre-wrap">{f.message}</p>
      {f.replies.map((r, i) => <p key={i} className="text-sm border-l-2 border-[#22C55E] pl-3 text-slate-300"><b>{r.by}:</b> {r.message}</p>)}
      <div className="flex gap-2">
        <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Öğretmene yanıt yaz" data-testid={`feedback-reply-${f.id}`} />
        <Button variant="outline" onClick={send} disabled={reply.trim().length < 3} data-testid={`feedback-reply-send-${f.id}`}><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

export const FeedbackPanel = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/feedback").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <section className="space-y-3" data-testid="feedback-panel">
      <h2 className="font-display text-2xl font-bold flex items-center gap-2"><MessageSquare className="h-6 w-6 text-[#FACC15]" />Öğretmen geri bildirimleri</h2>
      {items.length === 0 && <p className="text-sm text-slate-400">Henüz geri bildirim yok.</p>}
      <div className="grid lg:grid-cols-2 gap-3">{items.map((f) => <FeedbackItem key={f.id} f={f} onChange={load} />)}</div>
    </section>
  );
};

export const LockButton = ({ kind, item, onChange }) => {
  const { teacher } = useAuth();
  if (!teacher?.developer) return null;
  const toggle = async (e) => {
    e.stopPropagation();
    try { await api.put(`/lock/${kind}/${item.id}`, { locked: !item.locked }); toast.success(item.locked ? "Kilit açıldı" : "Kullanıma kapatıldı"); onChange(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Button size="sm" variant="ghost" onClick={toggle} title={item.locked ? "Kilidi aç" : "Kullanıma kapat"} data-testid={`lock-${kind}-${item.id}`}>
      {item.locked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
    </Button>
  );
};

export const LockedBadge = ({ item }) => (item.locked ? <span className="inline-flex items-center gap-1 text-xs rounded-full bg-slate-700/70 text-slate-200 px-2 py-0.5" data-testid={`locked-${item.id}`}><Lock className="h-3 w-3" />Kilitli</span> : null);
export const useBlocked = () => { const { teacher } = useAuth(); return (item) => Boolean(item?.locked) && !teacher?.developer; };

const CatalogRow = ({ kind, k, base, cur, onSaved }) => {
  const [f, setF] = useState({ label: cur.label || "", description: cur.description || "", locked: Boolean(cur.locked) });
  const save = async (patch = {}) => {
    const body = { ...f, ...patch };
    setF(body);
    try { await api.put(`/catalog/${kind}/${k}`, body); await reloadCatalog(); toast.success("Kaydedildi"); onSaved?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={`panel p-4 space-y-2 ${f.locked ? "opacity-70" : ""}`} data-testid={`catalog-${kind}-${k}`}>
      <div className="flex items-center gap-2">
        <Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder={base.label} data-testid={`catalog-label-${k}`} />
        <label className="flex items-center gap-2 text-sm shrink-0"><Switch checked={f.locked} onCheckedChange={(v) => save({ locked: v })} data-testid={`catalog-lock-${k}`} />Kilitli</label>
      </div>
      <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder={base.desc} rows={2} data-testid={`catalog-desc-${k}`} />
      <Button size="sm" onClick={() => save()} data-testid={`catalog-save-${k}`}>Kaydet</Button>
    </div>
  );
};

const UpcomingPanel = () => {
  const cat = useCatalog();
  const [list, setList] = useState(null);
  useEffect(() => { if (!list && cat.upcoming) setList(cat.upcoming); }, [cat.upcoming, list]);
  if (!list) return null;
  const set = (i, p) => setList(list.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const save = async () => {
    try {
      await api.put("/catalog/upcoming", list.filter((x) => x.label.trim()).map((x) => ({ ...x, key: x.key || x.label.toLowerCase().replace(/[^a-z0-9]/g, "") || `mod${Date.now()}` })));
      await reloadCatalog();
      toast.success("Yakında gelecek modlar kaydedildi");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="upcoming-panel">
      <p className="eyebrow">Yakında gelecek modlar · öğretmenlere yalnızca görünür, başlatılamaz</p>
      <div className="grid lg:grid-cols-2 gap-3">
        {list.map((u, i) => (
          <div key={i} className="panel p-4 space-y-2" data-testid={`upcoming-${i}`}>
            <div className="flex gap-2">
              <Input value={u.label} onChange={(e) => set(i, { label: e.target.value })} placeholder="Mod adı" data-testid={`upcoming-label-${i}`} />
              <Button variant="ghost" className="text-red-400" onClick={() => setList(list.filter((_, j) => j !== i))} data-testid={`upcoming-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <Textarea value={u.description} onChange={(e) => set(i, { description: e.target.value })} rows={4} placeholder={'Oyunun mantığı ("i" açıklaması)'} data-testid={`upcoming-desc-${i}`} />
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setList([...list, { key: "", label: "", description: "" }])} data-testid="upcoming-add">Mod ekle</Button>
        <Button size="sm" onClick={save} data-testid="upcoming-save">Kaydet</Button>
      </div>
    </div>
  );
};

export const CatalogPanel = () => {
  const cat = useCatalog();
  const [modes, setModes] = useState([]);
  const [n, setN] = useState(0);
  useEffect(() => { api.get("/modes").then((r) => setModes(r.data)).catch(() => {}); }, [n]);
  return (
    <section className="space-y-4" data-testid="catalog-panel">
      <h2 className="font-display text-2xl font-bold">Modlar ve etkinlik türleri</h2>
      <p className="text-sm text-slate-400">Başlık ve açıklamalar öğretmenlere "i" ipucu olarak görünür. Boş bırakılırsa varsayılan kullanılır. Kilitli olanlar öğretmenlere soluk ve kilitli görünür, kullanılamaz.</p>
      <p className="eyebrow">Oyun modları</p>
      <div className="grid lg:grid-cols-2 gap-3">
        {modes.map((m) => <CatalogRow key={m.key} kind="modes" k={m.key} base={{ label: m.label, desc: m.description }} cur={cat.modes[m.key] || {}} onSaved={() => setN((x) => x + 1)} />)}
      </div>
      <UpcomingPanel />
      <p className="eyebrow">Etkinlik türleri</p>
      <div className="grid lg:grid-cols-2 gap-3">
        {Object.entries(templateDefaults).map(([k, t]) => <CatalogRow key={k} kind="templates" k={k} base={t} cur={cat.templates[k] || {}} />)}
      </div>
    </section>
  );
};

export const MaintenancePanel = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/site/admin").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  const link = `${window.location.origin}/gelistirici/${s.dev_key}`;
  const toggle = async (v) => {
    try { await api.put("/site/maintenance", { maintenance: v }); setS({ ...s, maintenance: v }); toast.success(v ? "Site bakıma alındı" : "Site yeniden açıldı"); if (v) localStorage.setItem(BYPASS, "1"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className={`panel p-5 space-y-3 ${s.maintenance ? "border-amber-400/70" : ""}`} data-testid="maintenance-panel">
      <div className="flex items-center gap-3">
        <Wrench className="h-6 w-6 text-[#FACC15]" />
        <h2 className="font-display text-2xl font-bold flex-1">Bakım modu</h2>
        <Switch checked={s.maintenance} onCheckedChange={toggle} data-testid="maintenance-toggle" />
      </div>
      <p className="text-sm text-slate-400">Açıkken öğretmenler ve öğrenciler bakım uyarısını görür; yalnızca bu özel bağlantıdan giren geliştirici panele erişir.</p>
      <div className="flex gap-2">
        <Input readOnly value={link} data-testid="dev-link" />
        <Button variant="outline" onClick={() => { navigator.clipboard.writeText(link); toast.success("Bağlantı kopyalandı"); }} data-testid="dev-link-copy"><Copy className="h-4 w-4" /></Button>
      </div>
    </section>
  );
};
